import api from "../utils/api";

/**
 * Permission service for API calls and permission utilities
 * Note: Permission checking is now done via AuthContext (useAuth hook)
 * Use the useAuth() hook for hasPermission, hasAnyPermission, getUserRole etc.
 */

/**
 * Show permission denied alert with user-friendly message
 * @param {string} action - Optional action being attempted (e.g., "create investors", "delete users")
 */
export const showPermissionDenied = (action = null) => {
  const message = action
    ? `You do not have permission to ${action}.`
    : "You do not have permission to access this feature.";
  alert(message);
};

/**
 * Show permission alert for API errors (403)
 * Use this in catch blocks for explicit user actions (button clicks, form submissions)
 * @param {object} error - Axios error object
 * @param {string} action - Optional action description
 */
export const showPermissionAlert = (error, action = null) => {
  if (error.response?.status === 403) {
    const message =
      error.response?.data?.message ||
      (action
        ? `You do not have permission to ${action}.`
        : "You do not have permission to access this feature.");
    alert(message);
    return true;
  }
  return false;
};

/**
 * User management API calls
 */
export const userService = {
  // Get all users
  getUsers: () => api.get("/api/users"),

  // Get available permissions
  getPermissions: () => api.get("/api/users/permissions"),

  // Create new user
  createUser: (userData) => api.post("/api/users", userData),

  // Update user
  updateUser: (userId, userData) => api.put(`/api/users/${userId}`, userData),

  // Delete user
  deleteUser: (userId) => api.delete(`/api/users/${userId}`),

  // Get user permissions
  getUserPermissions: (userId) => api.get(`/api/users/${userId}/permissions`),

  // Grant permission
  grantPermission: (userId, permission) =>
    api.post(`/api/users/${userId}/permissions`, { permission }),

  // Revoke permission
  revokePermission: (userId, permission) =>
    api.delete(`/api/users/${userId}/permissions/${permission}`),
};
